import { memo, useMemo } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { t } from 'ttag'

import { borderOutlineStyles } from '@h2/components/inputs/styles'

import { getIntArray } from './utils'

import { PB80T, PB97T, WHITE, PB } from '../../colors'
import { Select } from '../../inputs/select'
import { RetractIcon } from '../../icons/retract'

const Wrap = styled.div`
  display: flex;
  position: sticky;
  bottom: 0;
  padding: 10px 32px;
  z-index: 20;
  justify-content: flex-end;
  align-items: center;
  background-color: ${PB97T};
`
const ProvidedContentWrap = styled.div`
  align-self: flex-start;
  align-items: center;
  display: flex;
  flex: 1;
  background-color: ${PB97T};
`
const PageSize = styled.div`
  display: flex;
  align-items: center;

  > span {
    font-size: 14px;
    margin-right: 10px;

    :nth-of-type(2) {
      margin-left: 10px;
    }
  }
`
const Button = styled.button`
  box-sizing: border-box;
  border: none;
  outline: 0;
  background: none;
  cursor: pointer;
  min-width: 35px;
  min-height: 35px;
  margin: 0;

  span {
    cursor: inherit;
    color: ${PB};
  }

  &:disabled {
    cursor: default;
    > span {
      color: ${PB80T};
    }
  }
`
const ButtonWrap = styled.div`
  display: flex;
  background-color: ${WHITE};
  border-radius: 5px;
  ${borderOutlineStyles}
`
const Previous = styled(RetractIcon)`
  transform: rotate(-90deg);
`
const Next = styled(RetractIcon)`
  transform: rotate(90deg);
`
const numPageButtons = 5
const pageRadius = Math.floor(numPageButtons / 2)
const lengths = [10, 25, 50, 100]
const PaginationComponent = ({
  page,
  total,
  pageSize,
  onChangePage,
  onChangePageSize,
  providedContent,
  isFetching = false
}) => {
  const pages = Math.ceil(total / pageSize)
  const options = useMemo(
    () => lengths.map(length => ({ label: `${length}`, value: length })),
    []
  )
  let pageSelections = getIntArray(Math.min(numPageButtons, pages))

  if (pages > numPageButtons) {
    const pageNum = page

    pageSelections = pageSelections.concat([null, pages])

    if (pageNum > pageRadius + 1 && pageNum < pages - pageRadius) {
      let counter = pageRadius

      pageSelections = [1, null]

      while (counter > 0) {
        pageSelections.push(pageNum - counter)
        counter--
      }

      while (counter <= pageRadius) {
        pageSelections.push(pageNum + counter)
        counter++
      }

      pageSelections.push(null, pages)
    }

    if (pageNum >= pages - pageRadius) {
      pageSelections = [1, null]

      for (let i = numPageButtons - 1; i >= 0; i--) {
        pageSelections.push(pages - i)
      }
    }
  }

  const firstRow = page * pageSize - pageSize + 1
  const lastRow = page * pageSize
  const minOfLastRow = Math.min(lastRow, total)
  const formattedTotal = total.toLocaleString()

  return (
    <Wrap>
      {providedContent && <ProvidedContentWrap>{providedContent}</ProvidedContentWrap>}
      <PageSize>
        <span>{t`Rows Per Page:`}</span>
        <Select
          size="small"
          width="75px"
          items={options}
          value={options[options.findIndex(option => option.value === pageSize)]}
          onChange={({ target }) => onChangePageSize(target.value.value)}
          menuDirection="up"
        />
        <span> {t`${firstRow} - ${minOfLastRow} of ${formattedTotal}`}</span>

        <ButtonWrap>
          <Button
            disabled={page === 1 || isFetching}
            onClick={() => onChangePage(page - 1)}>
            <Previous size="tiny" />
          </Button>
          <Button
            disabled={page === pages || isFetching}
            onClick={() => onChangePage(page + 1)}>
            <Next size="tiny" />
          </Button>
        </ButtonWrap>
      </PageSize>
    </Wrap>
  )
}
const Pagination = memo(PaginationComponent)

PaginationComponent.propTypes = {
  page: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  isFetching: PropTypes.bool,
  onChangePage: PropTypes.func.isRequired,
  onChangePageSize: PropTypes.func.isRequired,
  providedContent: PropTypes.object
}
Pagination.lengths = lengths

export { PaginationComponent, Pagination }
